// Null-safe formatting utilities to eliminate toFixed crashes
export const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : null;
};

export const safeToFixed = (v: unknown, digits = 2, na = "—"): string => {
  const n = toNum(v);
  return n === null ? na : n.toFixed(digits);
};

export const formatNum = (v: unknown, decimals = 2, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  return n.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
};

export const formatUSD = (v: unknown, decimals = 2, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  
  // Handle different price ranges with appropriate decimals
  if (n >= 1e12) return `$${safeToFixed(n / 1e12, 2)}T`;
  if (n >= 1e9) return `$${safeToFixed(n / 1e9, 2)}B`;
  if (n >= 1e6) return `$${safeToFixed(n / 1e6, 2)}M`;
  if (n >= 1e3) return `$${safeToFixed(n / 1e3, 2)}K`;
  return `$${safeToFixed(n, decimals)}`;
};

export const formatPrice = (v: unknown, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  
  if (n >= 1) return `$${safeToFixed(n, 2)}`;
  if (n >= 0.01) return `$${safeToFixed(n, 4)}`;
  return `$${safeToFixed(n, 8)}`;
};

export const formatCompact = (v: unknown, decimals = 2, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  return Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: decimals,
  }).format(n);
};

export const formatPercent = (v: unknown, digits = 1, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  const sign = n >= 0 ? '+' : '';
  return `${sign}${safeToFixed(n, digits)}%`;
};

export const formatPercentSimple = (v: unknown, digits = 1, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  return `${safeToFixed(n, digits)}%`;
};

export const formatRatio = (v: unknown, decimals = 2, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  if (n > 1000) return `${safeToFixed(n / 1000, 1)}K`;
  return safeToFixed(n, decimals);
};

export const formatTime = (v: unknown, na = "—"): string => {
  const n = toNum(v);
  if (n === null) return na;
  return `${safeToFixed(n / 1000, 1)}s`;
};

// For chart libraries: returns number or fallback (e.g., NaN for visual "holes")
export const numOr = (v: unknown, fallback = NaN): number => toNum(v) ?? fallback;

// Get change color class based on value
export const getChangeColor = (value: unknown) => {
  const n = toNum(value);
  if (n === null) return 'text-muted-foreground';
  return n >= 0 ? 'text-emerald-500' : 'text-red-500';
};